import { queryOptions } from '@tanstack/react-query';
import { registerBaseURL } from '#constants/hosts';
import { client } from '#queries/utils';
import { ApiGetStakeholderResponse } from '#proto/qonto/registration/stakeholders/firstparty/v1/get_stakeholder';
import { ApiListStakeholdersResponse } from '#proto/qonto/registration/stakeholders/firstparty/v1/list_stakeholders';
import { ApiListLegalEntitiesResponse as ApiListLegalEntitiesResponseV1 } from '#proto/qonto/registration/legal_entities/firstparty/v1/list_legal_entities';
import type { ApiLegalEntity } from '#proto/index.qonto.registration.legal_entities.firstparty.v3';
import { unwrapResponse } from '../../utils/unwrap-response';

// Maps a v1 item (JSON-API shape: { id, type, attributes: { legalName } })
// to a v3 ApiLegalEntity (flat shape) as required by SelectBeneficialOwners.
// SelectBeneficialOwners only reads `legalName`; other v3 required fields get defaults.
function v1ToV3LegalEntity(le: {
  id: string;
  attributes?: { legalName?: string } | null;
}): ApiLegalEntity {
  return {
    id: le.id,
    legalName: le.attributes?.legalName ?? '',
    shareCapitalOrigin: [],
    organizationId: '',
  };
}

export const registerLegalEntitiesQueries = {
  all: ['register-legal-entities'] as const,
  listByOrganization: (organizationId: string) =>
    queryOptions({
      queryKey: [...registerLegalEntitiesQueries.all, 'list', organizationId] as const,
      queryFn: async (): Promise<readonly ApiLegalEntity[]> => {
        const json = await client(
          `${registerBaseURL}/v1/organizations/${organizationId}/legal_entities`
        );
        return ApiListLegalEntitiesResponseV1.fromJSON(json).data.map(v1ToV3LegalEntity);
      },
    }),
};

export const membershipQueries = {
  all: ['register-memberships'] as const,
  list: (organizationId: string) =>
    queryOptions({
      queryKey: [...membershipQueries.all, 'list', organizationId] as const,
      queryFn: async () => {
        const json = await client(
          `${registerBaseURL}/v1/stakeholders?organization_id=${organizationId}`
        );

        return unwrapResponse(ApiListStakeholdersResponse.fromJSON(json), 'stakeholders');
      },
    }),
  item: (id: string) =>
    queryOptions({
      queryKey: [...membershipQueries.all, 'item', id] as const,
      queryFn: async () => {
        const json = await client(`${registerBaseURL}/v1/stakeholders/${id}`);

        return unwrapResponse(ApiGetStakeholderResponse.fromJSON(json), 'stakeholder');
      },
    }),
};
